// GA4 analytics module - single home for all custom events.
// Safe no-ops when gtag is unavailable; gated to the production hostname
// so preview/local testing never pollutes the real GA4 property.

const PROD_HOSTS = ['reshapers.co', 'www.reshapers.co'];

const isEnabled = () => {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return false;
    if (PROD_HOSTS.includes(window.location.hostname)) return true;
    return localStorage.getItem('ga_debug') === '1'; // opt-in for testing
  } catch {
    return false;
  }
};

// GA4 doesn't support XCG (Caribbean guilder) yet; ANG is 1:1 by design.
export const gaCurrency = (c) => ((c || 'ANG').toUpperCase() === 'XCG' ? 'ANG' : (c || 'ANG').toUpperCase());

export const gaEvent = (name, params = {}) => {
  if (!isEnabled()) return;
  try { window.gtag('event', name, params); } catch { /* never break the page */ }
};

// ---- Consent Mode v2 ----
export const initAnalyticsConsent = () => {
  try {
    window.dataLayer = window.dataLayer || [];
    const consented = localStorage.getItem('reshapers_cookie_consent') === 'accepted';
    if (!consented) {
      window.addEventListener('reshapers-consent-accepted', () => {
        if (typeof window.gtag === 'function') {
          window.gtag('consent', 'update', {
            analytics_storage: 'granted',
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
          });
        }
      }, { once: true });
    }
  } catch { /* noop */ }
};

// ---- Site-wide contact click listener (tel:, mailto:, WhatsApp) ----
export const initContactClickTracking = () => {
  if (window.__contactClickTrackingInit) return;
  window.__contactClickTrackingInit = true;
  document.addEventListener('click', (e) => {
    const a = e.target.closest?.('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    let method = null;
    if (href.startsWith('tel:')) method = 'phone';
    else if (href.startsWith('mailto:')) method = 'email';
    else if (href.includes('wa.me') || href.includes('api.whatsapp.com')) method = 'whatsapp';
    if (method) gaEvent('contact_click', { method });
  }, { capture: true, passive: true });
};

// ---- GA cookie readers (for stitching server-side purchases to sessions) ----
export const getGaClientId = () => {
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!m) return null;
    const parts = m[1].split('.');
    return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
  } catch { return null; }
};

export const getGaSessionId = () => {
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=([^;]+)/);
    if (!m) return null;
    // GS1.1.<session_id>.<...> or GS2.1.s<session_id>$...
    const raw = m[1].split('.')[2] || '';
    const sid = raw.replace(/^s/, '').split('$')[0];
    return /^\d+$/.test(sid) ? sid : null;
  } catch { return null; }
};

// ---- Ecommerce ----
const item = ({ id, name, category, price, quantity = 1 }) => ({
  item_id: id, item_name: name, item_category: category,
  price: Number(price) || 0, quantity,
});

export const trackViewItem = ({ id, name, category, price, currency }) => {
  gaEvent('view_item', {
    currency: gaCurrency(currency), value: Number(price) || 0,
    items: [item({ id, name, category, price })],
  });
};

export const trackBeginCheckout = ({ id, name, category, price, currency, quantity = 1 }) => {
  gaEvent('begin_checkout', {
    currency: gaCurrency(currency), value: (Number(price) || 0) * quantity,
    items: [item({ id, name, category, price, quantity })],
  });
};

export const trackAddPaymentInfo = ({ id, name, category, price, currency, quantity = 1 }) => {
  gaEvent('add_payment_info', {
    currency: gaCurrency(currency), value: (Number(price) || 0) * quantity,
    payment_type: 'sentoo',
    items: [item({ id, name, category, price, quantity })],
  });
};

// ---- Leads & engagement ----
export const trackGenerateLead = (formName) => gaEvent('generate_lead', { form_name: formName });
export const trackFormSubmit = (formName) => gaEvent('form_submit', { form_name: formName });
export const trackNewsletterSignup = () => gaEvent('sign_up', { method: 'newsletter' });
export const trackToolStarted = (toolName) => gaEvent('tool_started', { tool_name: toolName });
export const trackToolCompleted = (toolName) => gaEvent('tool_completed', { tool_name: toolName });
export const trackFileDownload = (fileName) => gaEvent('file_download', { file_name: fileName });
export const trackInsightRead = (articleTitle) => gaEvent('insight_read', { article_title: articleTitle });
